import React, { useEffect, useRef, useState } from 'react';
import { Badge, SectionTitle } from '../../design-system';
import { useConfirmAction } from '../ui/confirmactionhandler';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatters';
import {
  apiErrorDetails,
  approveProposal,
  editProposal,
  rejectProposal,
  runProposalTests,
  useLessonProposal,
} from '../../api/lessonreview';
import {
  CATEGORY_LABELS,
  categoryLabel,
  displayName,
  failingOutcomes,
  KIND_LABELS,
  ORIGIN_LABELS,
  scrubWarningLabel,
  scrubWarnings,
  sessionTypeLabel,
  STATUS_VARIANTS,
  statusLabel,
  TEST_FAILING_OUTCOMES,
  TEST_OUTCOME_HINTS,
  TEST_OUTCOME_LABELS,
  TEST_OUTCOME_VARIANTS,
  TEST_VERDICT_LABELS,
  TEST_VERDICT_VARIANTS,
  testGateProblem,
  testState,
  TIER_LABELS,
  tierLabel,
} from './lessonReviewLabels';
import ReviewWithLiviButton from './ReviewWithLiviButton';
import styles from './LessonReview.module.css';

// Mirror agent/lesson_proposals.py (MAX_SCENARIOS, MAX_EXAMPLE_CHARS, MAX_SCENARIO_CHARS).
const MAX_SCENARIOS = 3;
const MAX_EXAMPLE_CHARS = 400;
const MAX_SCENARIO_CHARS = 300;

const EVENT_LABELS = {
  submitted: 'Suggested',
  supported: 'Suggested again',
  authored: 'Written by a super-admin',
  edited: 'Edited by reviewer',
  tests_requested: 'Tests started',
  tested: 'Tested',
  approved_live: 'Approved — live',
  approved_staged: 'Approved — staged for release',
  rejected: 'Rejected',
  rejected_at_intake: 'Rejected automatically (privacy check)',
};

function Fact({ label, children }) {
  return (
    <>
      <span className={styles.factLabel}>{label}</span>
      <span className={styles.factValue}>{children}</span>
    </>
  );
}

function ErrorNotice({ error }) {
  if (!error) return null;
  const tierIndex = error.details?.tier_index;
  return (
    <div className={styles.errorBanner} role="alert">
      <div>{error.message}</div>
      {Array.isArray(tierIndex) && tierIndex.length > 0 ? (
        <>
          <div>Always-apply lessons already using this firm&apos;s space:</div>
          <ul className={styles.list}>
            {tierIndex.map((l) => (
              <li key={l.lesson_id}>{l.title || l.lesson_id}</li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function suggestedAsLabel(mandatory) {
  return mandatory ? 'Always apply' : 'Look up when relevant';
}

function costLabel(value) {
  const n = Number(value);
  return value === null || value === undefined || !Number.isFinite(n) ? null : `$${formatCurrency(n)}`;
}

/** "did it in 2 of 3 runs (1 failed to run)" for one arm of one scenario. */
function armLine(arm) {
  const extras = [];
  if (Number(arm?.not_tested || 0) > 0) extras.push(`${Number(arm.not_tested)} never got that far`);
  if (Number(arm?.errors || 0) > 0) extras.push(`${Number(arm.errors)} failed to run`);
  if (arm?.incomplete) extras.push('ran out of time');
  return `did it in ${Number(arm?.passed || 0)} of ${Number(arm?.runs || 0)} runs`
    + (extras.length ? ` (${extras.join(', ')})` : '');
}

/** The judge's reasons and one of Livi's replies, per arm. Collapsed when the scenario passed (the outcome line is
 * enough); open when it didn't, because that evidence is what the reviewer acts on. */
function RunDetails({ result, open = false }) {
  const arms = [['With the lesson', result.with], ['Without it', result.without]]
    .filter(([, arm]) => (arm?.reasons || []).length > 0 || (arm?.samples || []).length > 0);
  if (arms.length === 0) return null;
  return (
    <details className={styles.runDetails} open={open}>
      <summary className={styles.runSummary}>What Livi said, and why each reply was judged that way</summary>
      {arms.map(([label, arm]) => (
        <div key={label} className={styles.runArm}>
          <span className={styles.factLabel}>{label}</span>
          {(arm.reasons || []).length > 0 ? (
            <ul className={styles.list}>
              {arm.reasons.map((reason, i) => <li key={`${i}-${reason}`}>{reason}</li>)}
            </ul>
          ) : null}
          {arm.samples?.[0] ? (
            <>
              <span className={styles.muted}>One of Livi&apos;s replies:</span>
              <p className={styles.userWords}>{arm.samples[0]}</p>
            </>
          ) : null}
        </div>
      ))}
    </details>
  );
}

/** Where this revision's tests stand, in one or two sentences. */
function TestStatus({ proposal, state }) {
  const report = proposal.eval_report;
  if (state === 'no_scenarios') {
    return (
      <p className={styles.rationale}>
        {proposal.test_spec ? 'This proposal has no test scenarios.'
          : 'This proposal has no test plan (it predates them, or was written here).'}
        {' '}Add an example and scenarios with Edit so the lesson can be tested.
      </p>
    );
  }
  if (state === 'not_tested') return <p className={styles.rationale}>Not tested yet.</p>;
  if (state === 'outdated') {
    return (
      <p className={styles.rationale}>
        This revision hasn&apos;t been tested — the last tests were of revision {report.revision}.
      </p>
    );
  }
  if (state === 'running') {
    const runs = Number(report.runs_per_arm || 3);
    return (
      <div className={styles.notice} role="status">
        Testing now: each scenario runs {runs} times with the lesson and {runs} times without, and a separate
        reviewer judges every reply. This updates on its own.
      </div>
    );
  }
  if (state === 'stalled') {
    return (
      <div className={styles.warningBanner} role="status">
        The tests have been running for over an hour, so they have probably stalled. Run them again.
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className={styles.warningBanner} role="status">
        {report.error || "The tests couldn't run."} Run them again.
      </div>
    );
  }
  const testedAs = report.tested_as || {};
  const cost = costLabel(report.cost_usd);
  return (
    <>
      <div className={styles.factGrid}>
        <Fact label="Result">
          <Badge variant={TEST_VERDICT_VARIANTS[report.verdict] || 'default'} size="sm">
            {TEST_VERDICT_LABELS[report.verdict] || report.verdict}
          </Badge>
        </Fact>
        <Fact label="Tested as">
          {testedAs.session_type ? `${sessionTypeLabel(testedAs.session_type)} · ` : ''}
          {suggestedAsLabel(testedAs.mandatory)}
        </Fact>
        <Fact label="Runs">
          {Number(report.runs_per_arm || 3)} with the lesson and {Number(report.runs_per_arm || 3)} without, per
          scenario
        </Fact>
        {report.finished_at ? <Fact label="Tested on">{formatDateTime(report.finished_at)}</Fact> : null}
        {cost ? <Fact label="Cost">{cost}</Fact> : null}
      </div>
      {report.verdict === 'inconclusive' ? (
        <p className={styles.rationale}>
          {failingOutcomes(report).not_tested
            ? "Some scenarios didn't test the lesson, so they need rewriting before it can pass."
            : "Some scenarios couldn't be judged — run the tests again."}
        </p>
      ) : null}
      {report.carried_from ? (
        <p className={styles.rationale}>
          These results carry over from revision {report.carried_from}: the later edit didn&apos;t change what
          Livi does.
        </p>
      ) : null}
    </>
  );
}

/** What the reviewer can do when a scenario didn't work or made things worse. Re-running alone tests the same wording,
 * so the way forward is an edit (lesson or scenario), an approval with a reason (firm only), or a rejection. */
function NextSteps({ proposal, onEdit, onOpenedChat }) {
  const failing = failingOutcomes(proposal.eval_report);
  if (Object.keys(failing).length === 0) return null;
  const isCentral = proposal.target_tier === 'central';
  return (
    <div className={styles.nextSteps} role="region" aria-label="What you can do">
      <span className={styles.nextStepsTitle}>What you can do</span>
      <ul className={styles.list}>
        <li>Read what Livi said under each scenario that didn&apos;t work — the reasons say what it did instead.</li>
        {failing.does_not_work ? (
          <li>
            If the lesson doesn&apos;t clearly cover that situation, edit the lesson: say when it applies and exactly
            what Livi should do.
          </li>
        ) : null}
        {failing.makes_worse ? (
          <li>
            Livi got it right without the lesson, so the lesson gets in the way there: edit it so it doesn&apos;t
            reach that situation, or reject it.
          </li>
        ) : null}
        {failing.not_tested ? (
          <li>
            A scenario that didn&apos;t test the lesson usually leaves something out, so Livi has to ask first. Reword
            it so Livi can act straight away: it runs as the first message in a small sample case.
          </li>
        ) : null}
        <li>
          If the scenario asks for more than the lesson does, or for something Livi can&apos;t do in this kind of
          chat, edit or remove the scenario.
        </li>
        <li>Saving an edit runs the tests again.</li>
        <li>
          {isCentral
            ? 'A Livisibly-wide lesson has to pass its tests before it can be approved.'
            : 'If what passed is enough, you can still approve it below with your reason — or reject it.'}
        </li>
        <li>
          Running the tests again without an edit tests the same wording, so expect the same result. It&apos;s for
          runs that failed or ran out of time.
        </li>
      </ul>
      <div className={styles.actionRow}>
        <button type="button" className={styles.secondaryBtn} onClick={onEdit}>
          Edit the lesson or scenarios
        </button>
        <ReviewWithLiviButton lessonProposalId={proposal.lesson_proposal_id}
                              label="Ask Livi to suggest an edit" onOpened={onOpenedChat} />
      </div>
    </div>
  );
}

/** The lesson's tests (Learning Loop 2.1 spec, 2.2 results): the worked example the user saw, the scenarios, and —
 * once run — what each scenario showed with and without the lesson. */
function TestsSection({ proposal, pending, editing, onEdit, onOpenedChat }) {
  const spec = proposal.test_spec;
  const scenarios = Array.isArray(spec?.scenarios) ? spec.scenarios : [];
  const state = testState(proposal);
  const report = proposal.eval_report;
  const results = state === 'done' ? report.scenarios || [] : [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const canRun = pending && scenarios.length > 0 && state !== 'running';
  async function handleRun() {
    setBusy(true);
    setError(null);
    try {
      await runProposalTests(proposal.lesson_proposal_id, proposal.revision);
    } catch (err) {
      setError(apiErrorDetails(err, "The tests couldn't be started."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.drawerSection}>
      <SectionTitle
        actions={canRun ? (
          <button type="button" className={styles.secondaryBtn} onClick={handleRun} disabled={busy}>
            {busy ? 'Starting…' : (['not_tested', 'outdated'].includes(state) ? 'Run tests' : 'Run tests again')}
          </button>
        ) : null}
      >
        Tests
      </SectionTitle>
      <ErrorNotice error={error} />
      <TestStatus proposal={proposal} state={state} />
      {spec?.example ? (
        <div className={styles.factGrid}>
          <Fact label="Worked example">{spec.example}</Fact>
        </div>
      ) : null}
      {scenarios.length > 0 ? (
        <ol className={styles.scenarioList} aria-label="Test scenarios">
          {scenarios.map((s, i) => {
            const result = results.find((r) => Number(r.index) === i);
            return (
              <li key={`${i}-${s.user_says}`} className={styles.scenario}>
                <Fact label="User says">{s.user_says}</Fact>
                <Fact label="Livi should">{s.expected}</Fact>
                {result ? (
                  <>
                    <Fact label="Result">
                      <span className={styles.scoreLine}>
                        <Badge variant={TEST_OUTCOME_VARIANTS[result.outcome] || 'default'} size="sm">
                          {TEST_OUTCOME_LABELS[result.outcome] || result.outcome}
                        </Badge>
                        <span>{TEST_OUTCOME_HINTS[result.outcome]}</span>
                      </span>
                    </Fact>
                    {result.with ? <Fact label="With the lesson">{armLine(result.with)}</Fact> : null}
                    {result.without ? <Fact label="Without it">{armLine(result.without)}</Fact> : null}
                    {result.error ? <Fact label="Problem">{result.error}</Fact> : null}
                    <RunDetails result={result} open={TEST_FAILING_OUTCOMES.includes(result.outcome)} />
                  </>
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : null}
      {pending && !editing && state === 'done' ? (
        <NextSteps proposal={proposal} onEdit={onEdit} onOpenedChat={onOpenedChat} />
      ) : null}
    </section>
  );
}

function EditForm({ proposal, onSaved, onCancel, focusBody = false }) {
  const isAmend = Boolean(proposal.amends_lesson_id);
  const [fields, setFields] = useState({
    title: proposal.title || '',
    body: proposal.body || '',
    rationale: proposal.rationale || '',
    category: proposal.category || 'procedure',
    target_tier: proposal.target_tier || 'customer',
    mandatory_suggested: Boolean(proposal.mandatory_suggested),
  });
  const [example, setExample] = useState(proposal.test_spec?.example || '');
  const [scenarios, setScenarios] = useState((proposal.test_spec?.scenarios || []).map((s) => (
    { user_says: s.user_says || '', expected: s.expected || '' })));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const bodyRef = useRef(null);

  // Opened from the tests' "What you can do": the form sits far above that button, so move focus (and the view) to it.
  useEffect(() => {
    if (focusBody) bodyRef.current?.focus();
  }, [focusBody]);

  const set = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target.value }));
  const setScenario = (index, key) => (e) => setScenarios((list) => list.map((s, i) => (
    i === index ? { ...s, [key]: e.target.value } : s)));
  const canSave = fields.title.trim() && fields.body.trim() && !saving;

  function payload() {
    const out = { ...fields };
    const cleaned = scenarios
      .map((s) => ({ user_says: s.user_says.trim(), expected: s.expected.trim() }))
      .filter((s) => s.user_says || s.expected);
    // Send the test plan when there is one — an empty one would fail validation on a proposal that never had one.
    if (proposal.test_spec || example.trim() || cleaned.length > 0) {
      out.test_spec = { example: example.trim(), scenarios: cleaned };
    }
    return out;
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await editProposal(proposal.lesson_proposal_id, payload(), proposal.revision, note.trim() || undefined);
      onSaved('Saved as a new revision.');
    } catch (err) {
      setError(apiErrorDetails(err, 'The edit could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className={styles.formGrid} onSubmit={handleSave}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="lesson-edit-title">Title</label>
        <input id="lesson-edit-title" className={styles.input} value={fields.title} maxLength={80}
               onChange={set('title')} />
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="lesson-edit-body">Lesson</label>
        <textarea id="lesson-edit-body" ref={bodyRef} className={styles.textarea} rows={6} maxLength={1200}
                  value={fields.body} onChange={set('body')} />
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="lesson-edit-rationale">Why it helps (optional)</label>
        <textarea id="lesson-edit-rationale" className={styles.textarea} rows={3} maxLength={500}
                  value={fields.rationale} onChange={set('rationale')} />
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="lesson-edit-category">Category</label>
        <select id="lesson-edit-category" className={styles.select} value={fields.category}
                onChange={set('category')}>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="lesson-edit-tier">Applies to</label>
        <select id="lesson-edit-tier" className={styles.select} value={fields.target_tier}
                onChange={set('target_tier')} disabled={isAmend}>
          {Object.entries(TIER_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        {isAmend ? (
          <span className={styles.muted}>A change to an existing lesson stays where that lesson lives.</span>
        ) : null}
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="lesson-edit-suggested-as">Suggested as</label>
        <select id="lesson-edit-suggested-as" className={styles.select}
                value={fields.mandatory_suggested ? 'always' : 'lookup'}
                onChange={(e) => setFields((f) => ({ ...f, mandatory_suggested: e.target.value === 'always' }))}>
          <option value="lookup">{suggestedAsLabel(false)}</option>
          <option value="always">{suggestedAsLabel(true)}</option>
        </select>
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="lesson-edit-example">Worked example</label>
        <textarea id="lesson-edit-example" className={styles.textarea} rows={3} maxLength={MAX_EXAMPLE_CHARS}
                  value={example} onChange={(e) => setExample(e.target.value)} />
        <span className={styles.muted}>What the user would say, and what Livi then does under this lesson.</span>
      </div>
      <fieldset className={styles.fieldset}>
        <legend className={styles.label}>Test scenarios (up to {MAX_SCENARIOS})</legend>
        <span className={styles.muted}>
          Each runs as the first message in a small sample case (two spouses, a child, a business, two accounts, a few
          transactions). Write something Livi can act on without asking for anything first.
        </span>
        {scenarios.map((s, i) => (
          <div key={`scenario-${i}`} className={styles.scenarioEdit}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor={`lesson-edit-scenario-${i}-says`}>
                Scenario {i + 1}: the user says
              </label>
              <input id={`lesson-edit-scenario-${i}-says`} className={styles.input} value={s.user_says}
                     maxLength={MAX_SCENARIO_CHARS} onChange={setScenario(i, 'user_says')} />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor={`lesson-edit-scenario-${i}-expected`}>
                Scenario {i + 1}: Livi should
              </label>
              <input id={`lesson-edit-scenario-${i}-expected`} className={styles.input} value={s.expected}
                     maxLength={MAX_SCENARIO_CHARS} onChange={setScenario(i, 'expected')} />
            </div>
            <div className={styles.actionRow}>
              <button type="button" className={styles.secondaryBtn}
                      onClick={() => setScenarios((list) => list.filter((_, j) => j !== i))}>
                Remove scenario {i + 1}
              </button>
            </div>
          </div>
        ))}
        {scenarios.length < MAX_SCENARIOS ? (
          <div className={styles.actionRow}>
            <button type="button" className={styles.secondaryBtn}
                    onClick={() => setScenarios((list) => [...list, { user_says: '', expected: '' }])}>
              Add scenario
            </button>
          </div>
        ) : null}
        <span className={styles.muted}>
          Saving a change to the title, the lesson, “Suggested as”, where it applies or the tests runs the tests
          again.
        </span>
      </fieldset>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="lesson-edit-note">Note about this edit (optional)</label>
        <input id="lesson-edit-note" className={styles.input} value={note} maxLength={200}
               onChange={(e) => setNote(e.target.value)} />
      </div>
      <ErrorNotice error={error} />
      <div className={styles.actionRow}>
        <button type="submit" className={styles.primaryBtn} disabled={!canSave}>
          {saving ? 'Saving…' : 'Save revision'}
        </button>
        <button type="button" className={styles.secondaryBtn} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ReviewActions({ proposal, onDone }) {
  const isCentral = proposal.target_tier === 'central';
  const firm = proposal.firm_name || 'this firm';
  const [alwaysApply, setAlwaysApply] = useState(Boolean(proposal.mandatory_suggested));
  const [reason, setReason] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // The tests gate approval (2.2): a Livisibly-wide lesson needs a pass for this revision, tested as it will apply;
  // a firm lesson can go ahead on the reviewer's own reason, which is kept with the approval.
  const problem = testGateProblem(proposal, alwaysApply);
  const needsOverride = Boolean(problem) && !isCentral;
  const approveBlocked = (isCentral && Boolean(problem)) || (needsOverride && !overrideReason.trim());

  async function doApprove() {
    setBusy(true);
    setError(null);
    try {
      await approveProposal(proposal.lesson_proposal_id, proposal.revision, {
        mandatory: alwaysApply,
        ...(needsOverride ? { testOverrideReason: overrideReason.trim() } : {}),
      });
      onDone(isCentral
        ? 'Approved. It is staged for the next Livisibly release and is not live yet.'
        : `Approved. It is now live for ${firm}’s cases (open chats pick it up within about a minute).`);
    } catch (err) {
      setError(apiErrorDetails(err, 'The approval did not go through.'));
    } finally {
      setBusy(false);
    }
  }

  async function doReject() {
    setBusy(true);
    setError(null);
    try {
      await rejectProposal(proposal.lesson_proposal_id, reason.trim());
      onDone('Rejected. The reason is kept so similar suggestions can be spotted later.');
    } catch (err) {
      setError(apiErrorDetails(err, 'The rejection did not go through.'));
    } finally {
      setBusy(false);
    }
  }

  const { handleConfirmAction: confirmApprove, ConfirmDialogElement: ApproveDialog } = useConfirmAction(doApprove, {
    title: isCentral ? 'Approve for the next release' : 'Approve and make live',
    getEntityName: () => proposal.title,
    confirmMessage: isCentral
      ? 'This stages the lesson for the next Livisibly release. Nothing changes for anyone until that release ships.'
      : `This makes the lesson live for every case ${firm} owns. You can roll it back later.${needsOverride
        ? ' Its tests have not passed — your reason is kept with the approval.' : ''}`,
    proceedButtonLabel: 'Approve',
  });

  const { handleConfirmAction: confirmReject, ConfirmDialogElement: RejectDialog } = useConfirmAction(doReject, {
    title: 'Reject lesson',
    getEntityName: () => proposal.title,
    confirmMessage: 'The reason you gave is kept with the suggestion.',
    proceedButtonLabel: 'Reject',
    destructive: true,
  });

  return (
    <div className={styles.formGrid}>
      <ErrorNotice error={error} />
      <label className={styles.checkboxRow}>
        <input type="checkbox" className={styles.checkbox} checked={alwaysApply}
               onChange={(e) => setAlwaysApply(e.target.checked)} />
        Always apply (load into every matching chat, not only when looked up)
      </label>
      {problem && isCentral ? (
        <div className={styles.warningBanner}>
          {problem.message}{' '}
          {problem.code === 'LESSON_TEST_MISMATCH'
            ? 'Set “Always apply” to match, or change “Suggested as” with Edit — that runs the tests again.'
            : 'A Livisibly-wide lesson can be approved only after its tests pass for this revision.'}
        </div>
      ) : null}
      {!problem && !isCentral && Boolean(proposal.eval_report?.tested_as?.mandatory) !== alwaysApply ? (
        <div className={styles.notice}>
          It was tested as “{suggestedAsLabel(proposal.eval_report?.tested_as?.mandatory)}”; approving it as
          “{suggestedAsLabel(alwaysApply)}” hasn&apos;t been tested.
        </div>
      ) : null}
      {needsOverride ? (
        <>
          <div className={styles.warningBanner}>
            {problem.message} You can still approve this firm lesson, but say why — the reason is kept with the
            approval.
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="lesson-override-reason">
              Reason to approve without passing tests
            </label>
            <textarea id="lesson-override-reason" className={styles.textarea} rows={2} maxLength={500}
                      value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
          </div>
        </>
      ) : null}
      <div className={styles.actionRow}>
        <button type="button" className={styles.primaryBtn} disabled={busy || proposal.stale_base || approveBlocked}
                onClick={() => confirmApprove(proposal)}>
          {isCentral ? 'Approve for next release' : 'Approve and make live'}
        </button>
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="lesson-reject-reason">Reason for rejecting</label>
        <textarea id="lesson-reject-reason" className={styles.textarea} rows={3} maxLength={500}
                  value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      <div className={styles.actionRow}>
        <button type="button" className={styles.dangerBtn} disabled={busy || !reason.trim()}
                onClick={() => confirmReject(proposal)}>
          Reject
        </button>
      </div>
      <ApproveDialog />
      <RejectDialog />
    </div>
  );
}

export default function ProposalDrawer({ lessonProposalId, onClose }) {
  const { data, error, isLoading } = useLessonProposal(lessonProposalId);
  const [editing, setEditing] = useState(false);
  const [focusBody, setFocusBody] = useState(false);
  const [message, setMessage] = useState(null);

  function startEditing(fromTests) {
    setMessage(null);
    setFocusBody(fromTests);
    setEditing(true);
  }

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const proposal = data?.proposal;
  const pending = proposal?.status === 'PENDING';
  const provenance = proposal?.provenance || {};
  const warnings = scrubWarnings(proposal?.scrub);
  const scrubRejected = Object.values(proposal?.scrub || {}).some((stage) => stage?.verdict === 'reject');
  const bundle = proposal?.result?.staged_bundle;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} aria-hidden="true" />
      <aside className={styles.drawer} role="dialog" aria-modal="true" aria-label="Lesson proposal">
        <div className={styles.drawerHeader}>
          <div>
            <h2 className={styles.drawerTitle}>{proposal?.title || 'Lesson proposal'}</h2>
            {proposal ? (
              <div className={styles.badgeRow}>
                <Badge variant={STATUS_VARIANTS[proposal.status] || 'default'} size="sm">
                  {statusLabel(proposal.status)}
                </Badge>
                <Badge variant={proposal.target_tier === 'central' ? 'accent' : 'info'} size="sm">
                  {tierLabel(proposal.target_tier)}
                </Badge>
                <Badge size="sm">{KIND_LABELS[proposal.kind] || proposal.kind}</Badge>
              </div>
            ) : null}
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close lesson proposal">
            ✕
          </button>
        </div>

        <div className={styles.drawerBody}>
          {isLoading ? <p className={styles.emptyText}>Loading…</p> : null}
          {error ? (
            <div className={styles.errorBanner} role="alert">
              {apiErrorDetails(error, 'This proposal could not be loaded.').message}
            </div>
          ) : null}
          {message ? <div className={styles.successBanner} role="status">{message}</div> : null}

          {proposal ? (
            <>
              <section className={styles.drawerSection}>
                <SectionTitle>Lesson</SectionTitle>
                {editing ? (
                  <EditForm proposal={proposal} focusBody={focusBody}
                            onSaved={(m) => { setEditing(false); setMessage(m); }}
                            onCancel={() => setEditing(false)} />
                ) : (
                  <>
                    <p className={styles.lessonBody}>{proposal.body}</p>
                    {proposal.rationale ? <p className={styles.rationale}>Why: {proposal.rationale}</p> : null}
                    <div className={styles.factGrid}>
                      <Fact label="Category">{categoryLabel(proposal.category)}</Fact>
                      <Fact label="Suggested as">
                        {proposal.mandatory_suggested ? 'Always apply' : 'Look up when relevant'}
                      </Fact>
                    </div>
                    {pending ? (
                      <div className={styles.actionRow}>
                        <button type="button" className={styles.secondaryBtn}
                                onClick={() => startEditing(false)}>
                          Edit
                        </button>
                      </div>
                    ) : null}
                  </>
                )}
              </section>

              {proposal.kind === 'amend' ? (
                <section className={styles.drawerSection}>
                  <SectionTitle>Change from the current version</SectionTitle>
                  {proposal.stale_base ? (
                    <div className={styles.warningBanner} role="alert">
                      The lesson changed after this was suggested. Edit this proposal so it is based on the
                      current version before approving.
                    </div>
                  ) : null}
                  <pre className={styles.code}>{proposal.diff || 'No changes recorded.'}</pre>
                </section>
              ) : null}

              <section className={styles.drawerSection}>
                <SectionTitle>Where it came from</SectionTitle>
                <div className={styles.factGrid}>
                  <Fact label="Source">{ORIGIN_LABELS[provenance.origin] || provenance.origin || '—'}</Fact>
                  {proposal.target_tier === 'customer' ? (
                    <Fact label="Firm">
                      {proposal.customer_id ? (
                        <span title={proposal.customer_id}>{displayName(proposal.firm_name, 'firm')}</span>
                      ) : '—'}
                    </Fact>
                  ) : null}
                  {provenance.source_case_id ? (
                    <Fact label="Case">
                      <span title={provenance.source_case_id}>
                        {displayName(provenance.source_case_name, 'case')}
                      </span>
                    </Fact>
                  ) : null}
                  {provenance.source_session_type ? (
                    <Fact label="Chat type">{sessionTypeLabel(provenance.source_session_type)}</Fact>
                  ) : null}
                  {provenance.proposer_user_id ? (
                    <Fact label="Suggested by">
                      <span title={provenance.proposer_user_id}>
                        {displayName(provenance.proposer_name, 'person')}
                      </span>
                    </Fact>
                  ) : null}
                  <Fact label="Suggested on">{proposal.created_at ? formatDate(proposal.created_at) : '—'}</Fact>
                  {Number(proposal.supporting_sources_count || 1) > 1 ? (
                    <Fact label="Times suggested">{Number(proposal.supporting_sources_count)}</Fact>
                  ) : null}
                </div>
              </section>

              <section className={styles.drawerSection}>
                <SectionTitle>Privacy check</SectionTitle>
                <p className={styles.rationale}>
                  {scrubRejected
                    ? 'The automatic check found specific names, numbers, addresses or ids, so this was rejected.'
                    : 'No specific names, numbers, addresses or ids were found.'}
                </p>
                {warnings.length > 0 ? (
                  <>
                    <p className={styles.rationale}>Worth a second look:</p>
                    <ul className={styles.list}>
                      {warnings.map((rule) => <li key={rule}>{scrubWarningLabel(rule)}</li>)}
                    </ul>
                  </>
                ) : null}
              </section>

              {(data.similar_rejected || []).length > 0 ? (
                <section className={styles.drawerSection}>
                  <SectionTitle>Similar suggestions that were rejected</SectionTitle>
                  <ul className={styles.list}>
                    {data.similar_rejected.map((s) => (
                      <li key={s.lesson_proposal_id}>
                        {s.title}{s.status_reason ? ` — ${s.status_reason}` : ''}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              <TestsSection proposal={proposal} pending={pending} editing={editing}
                            onEdit={() => startEditing(true)} onOpenedChat={onClose} />

              {proposal.user_words ? (
                <section className={styles.drawerSection}>
                  <SectionTitle>In the user&apos;s own words</SectionTitle>
                  <p className={styles.userWords}>{proposal.user_words}</p>
                  <p className={styles.rationale}>
                    Read from the case&apos;s records for this review. These words stay with the case and are never
                    copied into the lesson.
                  </p>
                </section>
              ) : null}

              {pending && !editing ? (
                <section className={styles.drawerSection}>
                  <SectionTitle>Talk it through</SectionTitle>
                  <p className={styles.rationale}>
                    Livi reads the proposal, checks it against the firm&apos;s existing lessons and gives you a
                    recommendation. Nothing changes without your confirmation.
                  </p>
                  <ReviewWithLiviButton lessonProposalId={proposal.lesson_proposal_id} onOpened={onClose} />
                </section>
              ) : null}

              {pending && !editing ? (
                <section className={styles.drawerSection}>
                  <SectionTitle>Decision</SectionTitle>
                  <ReviewActions proposal={proposal} onDone={setMessage} />
                </section>
              ) : null}

              {proposal.status === 'APPROVED_LIVE' && proposal.result ? (
                <section className={styles.drawerSection}>
                  <SectionTitle>Decision</SectionTitle>
                  <p className={styles.rationale}>
                    Live for {proposal.firm_name || 'this firm'}&apos;s cases as version {proposal.result.version}
                    {proposal.reviewer_user_id ? ` — approved by ${displayName(proposal.reviewer_name, 'person')}` : ''}.
                  </p>
                  {proposal.result.test_override_reason ? (
                    <p className={styles.rationale}>
                      Approved without passing tests: {proposal.result.test_override_reason}
                    </p>
                  ) : null}
                </section>
              ) : null}

              {proposal.status === 'APPROVED_STAGED' && bundle ? (
                <section className={styles.drawerSection}>
                  <SectionTitle>Staged for the next release</SectionTitle>
                  <p className={styles.rationale}>
                    Not live. This file ships with the next Livisibly release: {bundle.target_path}
                  </p>
                  <pre className={styles.code}>{bundle.file_content}</pre>
                </section>
              ) : null}

              {proposal.status === 'REJECTED' ? (
                <section className={styles.drawerSection}>
                  <SectionTitle>Decision</SectionTitle>
                  <p className={styles.rationale}>
                    Rejected
                    {proposal.reviewer_user_id ? ` by ${displayName(proposal.reviewer_name, 'person')}` : ''}
                    : {proposal.status_reason || 'no reason recorded'}
                  </p>
                </section>
              ) : null}

              <section className={styles.drawerSection}>
                <SectionTitle>History</SectionTitle>
                <ol className={styles.timeline}>
                  {(data.events || []).map((ev, i) => (
                    <li key={`${ev.ts}-${ev.action}-${i}`} className={styles.timelineItem}>
                      <span>
                        {EVENT_LABELS[ev.action] || ev.action}
                        {ev.action === 'tested' && ev.verdict
                          ? ` — ${TEST_VERDICT_LABELS[ev.verdict] || ev.verdict}` : ''}
                        {ev.test_override_reason ? ` — without passing tests: ${ev.test_override_reason}` : ''}
                      </span>
                      <span className={styles.timelineMeta}>
                        {ev.ts ? formatDate(ev.ts) : ''}{ev.actor ? ` · ${displayName(ev.actor_name, 'person')}` : ''}
                        {ev.revision ? ` · revision ${ev.revision}` : ''}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            </>
          ) : null}
        </div>
      </aside>
    </>
  );
}
